import React from "react";
import { SERVER_URL } from "../config/config.js";
import "./biltyReport.css";
import ReportTable from "./ReportTable";
import ReactHTMLTableToExcel from "react-html-table-to-excel";
import Popup from "reactjs-popup";
import { useRef, useState } from "react";
import { parseDateOnly } from "../utils/documentExpiry.js";

let ex_data = [];

const DAY_MS = 24 * 60 * 60 * 1000;

const getValidityStatus = (value) => {
  const expiry = parseDateOnly(value);
  if (!expiry) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((expiry - today) / DAY_MS);
  if (diffDays < 0) return "expired";
  if (diffDays <= 15) return "warning";
  return "valid";
};

const renderValidityCell = ({ value }) => {
  const status = getValidityStatus(value);
  return (
    <div className={`validity-cell${status ? ` validity-${status}` : ""}`}>
      {value || "-"}
    </div>
  );
};

const DOC_TYPES = {
  rc_book: { label: "RC Book" },
  insurance: { label: "Insurance" },
  puc: { label: "PUC" },
  permit: { label: "Permit" },
};

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "bmp", "webp"];

const previewContentStyle = {
  maxWidth: "960px",
  width: "95%",
  height: "90vh",
  padding: "0px",
  border: "none",
  background: "transparent",
  overflow: "hidden",
};

const buildDocUrl = (value) => {
  const baseUrl = SERVER_URL.endsWith("/")
    ? SERVER_URL.slice(0, -1)
    : SERVER_URL;
  let cleanPath = String(value || "").replace(/^\/?api\/v1/, "");
  if (!cleanPath.startsWith("/")) {
    cleanPath = `/${cleanPath}`;
  }
  const sep = cleanPath.includes("?") ? "&" : "?";
  return `${baseUrl}${cleanPath}${sep}t=${Date.now()}`;
};

const getDocPreviewType = (docType, row) => {
  const pathField = `${docType}_path`;
  const path = row ? row[pathField] : "";
  const ext = String(path || "").split(".").pop().toLowerCase();
  if (ext === "pdf") return "pdf";
  if (IMAGE_EXTENSIONS.includes(ext)) return "image";
  return "auto";
};

function VehicleManagementReport({ sessionObject }) {
  const renderDocCell = (docType) => ({ value, row }) => {
    if (!value) {
      return <span className="report-doc-empty">-</span>;
    }
    return (
      <button
        type="button"
        className="preview-doc-btn"
        onClick={(e) => {
          e.stopPropagation();
          openPreview({ vehicleNo: row.original.vehicle_no, docType, row: row.original });
        }}
      >
        Preview
      </button>
    );
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "Vehicle No",
        accessor: "vehicle_no",
        width: "155px",
        minWidth: "110px",
        canFilter: true,
      },
      {
        Header: "Driver Name",
        accessor: "driver_name",
        width: "150px",
        minWidth: "120px",
        canFilter: true,
        disableSortBy: true,
      },
      {
        Header: "Driver No",
        accessor: "driver_no",
        width: "120px",
        minWidth: "120px",
        canFilter: true,
        disableSortBy: true,
      },
      {
        Header: "Owner Name",
        accessor: "owner_name",
        width: "155px",
        minWidth: "130px",
        canFilter: true,
      },
      {
        Header: "RC Book",
        accessor: "rc_book",
        width: "100px",
        minWidth: "100px",
        canFilter: false,
        disableSortBy: true,
        Cell: renderDocCell("rc_book"),
      },
      {
        Header: "Permit",
        accessor: "permit",
        width: "100px",
        minWidth: "100px",
        canFilter: false,
        disableSortBy: true,
        Cell: renderDocCell("permit"),
      },
      {
        Header: "Permit Validity",
        accessor: "permit_validity",
        width: "155px",
        minWidth: "130px",
        canFilter: true,
        disableSortBy: true,
        filterType: "validity",
        Cell: renderValidityCell,
      },
      {
        Header: "PUC",
        accessor: "puc",
        width: "100px",
        minWidth: "100px",
        canFilter: false,
        disableSortBy: true,
        Cell: renderDocCell("puc"),
      },
      {
        Header: "PUC Validity",
        accessor: "puc_validity",
        width: "155px",
        minWidth: "130px",
        canFilter: true,
        disableSortBy: true,
        filterType: "validity",
        Cell: renderValidityCell,
      },
      {
        Header: "License No",
        accessor: "license_no",
        width: "150px",
        minWidth: "140px",
        canFilter: true,
        disableSortBy: true,
      },
      // {
      //   Header: "Insurance Company",
      //   accessor: "insurance_company",
      //   width: "150px",
      //   minWidth: "150px",
      //   canFilter: true,
      // },
      {
        Header: "License Validity",
        accessor: "license_validity",
        width: "130px",
        minWidth: "130px",
        canFilter: true,
        disableSortBy: true,
      },
      {
        Header: "Insurance",
        accessor: "insurance",
        width: "100px",
        minWidth: "100px",
        canFilter: false,
        disableSortBy: true,
        Cell: renderDocCell("insurance"),
      },
      {
        Header: "Insurance Validity",
        accessor: "insurance_validity",
        width: "150px",
        minWidth: "140px",
        canFilter: true,
        disableSortBy: true,
        filterType: "validity",
        Cell: renderValidityCell,
      },


    ],
    []
  );

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const fetchIdRef = useRef(0);
  const download_ref = useRef(null);
  const fullRowsRef = useRef([]);

  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState("auto");

  const openPreview = ({ vehicleNo, docType, row }) => {
    const pathValue = row ? row[docType] : null;
    if (!pathValue) return;
    setPreviewDoc({
      vehicleNo,
      docType,
      label: (DOC_TYPES[docType] && DOC_TYPES[docType].label) || docType,
    });
    setPreviewType(getDocPreviewType(docType, row));
    setPreviewUrl(buildDocUrl(pathValue));
  };

  const closePreview = () => {
    setPreviewDoc(null);
    setPreviewUrl(null);
    setPreviewType("auto");
  };

  const buildRows = (resp) => {
    let rows = [];
    for (let vehicleNo in resp) {
      let entry = resp[vehicleNo];
      let row = { ...entry.vehicle };
      if (entry.documents) {
        row.rc_book = entry.documents.rc_book;
        row.insurance = entry.documents.insurance;
        row.puc = entry.documents.puc;
        row.permit = entry.documents.permit;
      }
      rows.push(row);
    }
    return rows;
  };

  const fetchData = React.useCallback(
    async ({ pageSize, pageIndex, sortBy, customFilters }) => {
      const fetchId = ++fetchIdRef.current;
      setLoading(true);

      if (fetchId === fetchIdRef.current) {
        if (fullRowsRef.current.length === 0) {
          let response = await fetch(SERVER_URL + "/vehicle/complete-info", {
            method: "GET",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          });
          let resp = await response.json();
          fullRowsRef.current = buildRows(resp);
          ex_data = fullRowsRef.current;
        }

        let rows = fullRowsRef.current;
        if (customFilters) {
          for (const column of columns) {
            const filterValue = customFilters[column.accessor];
            if (!filterValue) continue;
            if (column.filterType === "validity") {
              const wantStatus = String(filterValue).toLowerCase();
              rows = rows.filter(
                (row) => getValidityStatus(row[column.accessor]) === wantStatus
              );
            } else {
              const filterText = String(filterValue).toLowerCase();
              rows = rows.filter((row) =>
                String(row[column.accessor] || "")
                  .toLowerCase()
                  .includes(filterText)
              );
            }
          }
        }
        if (sortBy && sortBy.length > 0) {
          rows = [...rows].sort((a, b) => {
            for (let sort of sortBy) {
              let av = a[sort.id];
              let bv = b[sort.id];
              if (av === bv) continue;
              if (av == null) return 1;
              if (bv == null) return -1;
              let cmp = String(av).localeCompare(String(bv), undefined, {
                numeric: true,
              });
              return sort.desc ? -cmp : cmp;
            }
            return 0;
          });
        }

        const startRow = pageSize * pageIndex;
        const endRow = startRow + pageSize;
        setData(rows.slice(startRow, endRow));
        setPageCount(Math.ceil(rows.length / pageSize));
        setLoading(false);
      }
    },
    []
  );

  return (
    <div className="report-bilty">
      <div className="form-header">Vehicle Management Report</div>
      <div className="report-bilty-table-container">
        <ReportTable
          columns={columns}
          data={data}
          fetchData={fetchData}
          loading={loading}
          pageCount={pageCount}
          // filterableColumns={["vehicle_no", "owner_name"]}
        />

        <Popup
          open={Boolean(previewDoc)}
          modal
          contentStyle={previewContentStyle}
          closeOnDocumentClick={false}
          onClose={closePreview}
        >
          <div className="pop-up-container vdoc-preview-modal">
            <div className="pop-up-header">
              <span style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ fontSize: "16px", fontWeight: "bold" }}>
                  {previewDoc ? previewDoc.label : ""} Preview
                </span>
                {previewDoc && previewDoc.vehicleNo && (
                  <span className="vdoc-vehicle-badge">{previewDoc.vehicleNo}</span>
                )}
              </span>
              <button
                type="button"
                className="vdoc-close-btn"
                onClick={closePreview}
                title="Close"
              >
                &times;
              </button>
            </div>
            <div className="vdoc-preview-body">
              {previewUrl && previewType === "image" ? (
                <img
                  className="vdoc-preview-img"
                  src={previewUrl}
                  alt={previewDoc ? previewDoc.label : "Document Preview"}
                />
              ) : previewUrl ? (
                <iframe
                  className="vdoc-preview-iframe"
                  src={previewUrl}
                  title={previewDoc ? previewDoc.label : "Document Preview"}
                />
              ) : (
                <div className="loader-container" style={{ height: "100%" }}>
                  <div className="spinner"></div>
                </div>
              )}
            </div>
            <div className="pop-up-actions">
              {previewDoc && previewUrl && (
                <button
                  type="button"
                  className="pop-up-button"
                  onClick={() => {
                    const link = document.createElement("a");
                    link.href = previewUrl;
                    link.download = `${previewDoc.vehicleNo}_${previewDoc.docType}`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                >
                  Download
                </button>
              )}
              <button className="pop-up-button" onClick={closePreview}>
                Close
              </button>
            </div>
          </div>
        </Popup>
        <div className="form-footer">
          <button style={{ display: "none" }}>
            <ReactHTMLTableToExcel
              id="test-table-xls-button"
              className="download-table-xls-button"
              table="vehicle_management_report_excel"
              filename="tablexls"
              sheet="tablexls"
              buttonText="Download as XLS"
              ref={(a) => (download_ref.current = a)}
            />
          </button>
          {/* <button
            className="download-table-xls-button"
            onClick={() => {
              download_ref.current.handleDownload();
            }}
          >
            Download as XLS
          </button> */}
          <table
            style={{ display: "none" }}
            id="vehicle_management_report_excel"
          >
            <thead>
              <tr>{columns.map((column) => (
                <th key={column.accessor}>{column.Header}</th>
              ))}</tr>
            </thead>
            <tbody>
              {ex_data.map((row, i) => (
                <tr key={i}>
                  {columns.map((column) => (
                    <td key={column.accessor}>
                      {row[column.accessor] != null ? row[column.accessor] : ""}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default VehicleManagementReport;